import React from 'react';
import './skeleton.css';

/**
 * Skeleton primitives — content-shaped loading placeholders.
 *
 * Use these instead of a spinner whenever the shape of the eventual content
 * is known. A spinner reserves no space, so the real content shifts the
 * layout when it lands; a skeleton occupies that space up front.
 *
 * Use a spinner (or a written progress state) instead when the wait is NOT
 * "content is arriving" — e.g. SubscriptionSuccessPage polls a webhook for
 * up to 15s. There is no shape to stand in for, and a shimmering block would
 * imply one is coming.
 *
 * ACCESSIBILITY: skeletons are decorative. Wrap a loading region in
 * <SkeletonRegion label="..."> so assistive tech hears one "Loading X"
 * announcement rather than reading out a pile of empty boxes.
 */

/** A single placeholder block. Pass width/height as any CSS length. */
export function Skeleton({ width, height, radius, className = '', style, ...rest }) {
  return (
    <span
      className={`skeleton ${className}`.trim()}
      style={{ width, height, borderRadius: radius, ...style }}
      {...rest}
    />
  );
}

/**
 * A run of prose lines. The last line is deliberately short so the block
 * reads as a paragraph instead of a stack of identical bars.
 */
export function SkeletonText({ lines = 3, width = '100%', lastLineWidth = '62%', gap, style, ...rest }) {
  return (
    <span className="skeleton-lines" style={{ width, gap, ...style }} {...rest}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          className="skeleton-text"
          width={i === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </span>
  );
}

/** A circular placeholder — avatars, score dials, status dots. */
export function SkeletonCircle({ size = 24, style, ...rest }) {
  return (
    <Skeleton
      className="skeleton-circle"
      width={size}
      height={size}
      style={style}
      {...rest}
    />
  );
}

/** A bordered panel to nest skeleton shapes inside, matching a real card. */
export function SkeletonPanel({ children, className = '', style, ...rest }) {
  return (
    <div className={`skeleton-panel ${className}`.trim()} style={style} {...rest}>
      {children}
    </div>
  );
}

/**
 * Wraps a loading region. Owns the single screen-reader announcement for
 * everything inside it, and marks the region busy so assistive tech knows
 * the content is provisional — `aria-busy` appeared nowhere in this app
 * before skeletons existed.
 */
export function SkeletonRegion({ label = 'Loading', children, className, style, ...rest }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={className}
      style={style}
      {...rest}
    >
      {/* The shapes themselves carry no meaning — hide them from the
          accessibility tree so the label above is the whole announcement. */}
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

export default Skeleton;
